import type { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import type { CapabilitySuggestion } from '@indutrust/capability-model';
import {
  resolveHttpError,
  withCrudEntitiesEvents,
  withOrderedEntities,
  withRequestStatus,
} from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore, withComputed, withState } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { from, switchMap } from 'rxjs';
import { CapabilitySuggestionApiClient } from '../capability-suggestion-api-client.service';
import {
  adminCapabilitySuggestionCrudEvents,
  adminCapabilitySuggestionWorkflowEvents,
} from './admin-capability-suggestion.events';

type AdminCapabilitySuggestionState = {
  workflowBusy: boolean;
};

/**
 * Admin store for capability-leaf suggestions — platform-wide review queue.
 *
 * Combines Pattern A CRUD ({@link adminCapabilitySuggestionCrudEvents}) with
 * approve / reject workflow events. Loaded on demand from
 * `/backoffice/capability-suggestions`.
 *
 * @example
 * const store = inject(AdminCapabilitySuggestionStore);
 * const dispatch = injectDispatch(adminCapabilitySuggestionCrudEvents);
 * dispatch.loadAllRequested({});
 */
export const AdminCapabilitySuggestionStore = signalStore(
  { providedIn: 'root' },
  withCrudEntitiesEvents<CapabilitySuggestion>(
    adminCapabilitySuggestionCrudEvents,
    () => inject(CapabilitySuggestionApiClient)
  ),
  withOrderedEntities<CapabilitySuggestion>(),
  withRequestStatus(),
  withState<AdminCapabilitySuggestionState>({
    workflowBusy: false,
  }),
  withComputed(({ entities }) => ({
    approvedEntries: computed(() =>
      entities().filter((entry) => entry.status === 'approved')
    ),
    pendingEntries: computed(() =>
      entities().filter((entry) => entry.status === 'pending')
    ),
    rejectedEntries: computed(() =>
      entities().filter((entry) => entry.status === 'rejected')
    ),
  })),
  withReducer(
    on(adminCapabilitySuggestionWorkflowEvents.approveRequested, () => ({
      workflowBusy: true,
    })),
    on(adminCapabilitySuggestionWorkflowEvents.rejectRequested, () => ({
      workflowBusy: true,
    })),
    on(adminCapabilitySuggestionWorkflowEvents.workflowSucceeded, () => ({
      workflowBusy: false,
    })),
    on(
      adminCapabilitySuggestionWorkflowEvents.workflowFailed,
      ({ payload }) => ({
        workflowBusy: false,
        requestStatus: { error: payload.error },
      })
    )
  ),
  withEventHandlers(
    (
      _store,
      events = inject(Events),
      client = inject(CapabilitySuggestionApiClient)
    ) => {
      const toFailed = (error: HttpErrorResponse) =>
        adminCapabilitySuggestionWorkflowEvents.workflowFailed({
          error: resolveHttpError(error).code,
        });

      return {
        approve$: events
          .on(adminCapabilitySuggestionWorkflowEvents.approveRequested)
          .pipe(
            switchMap(({ payload }) =>
              client.approve(payload.id).pipe(
                mapResponse({
                  next: (suggestion) =>
                    adminCapabilitySuggestionWorkflowEvents.workflowSucceeded({
                      suggestion,
                    }),
                  error: toFailed,
                })
              )
            )
          ),

        reject$: events
          .on(adminCapabilitySuggestionWorkflowEvents.rejectRequested)
          .pipe(
            switchMap(({ payload }) =>
              client.reject(payload.id, payload.reason).pipe(
                mapResponse({
                  next: (suggestion) =>
                    adminCapabilitySuggestionWorkflowEvents.workflowSucceeded({
                      suggestion,
                    }),
                  error: toFailed,
                })
              )
            )
          ),

        refreshOnWorkflow$: events
          .on(adminCapabilitySuggestionWorkflowEvents.workflowSucceeded)
          .pipe(
            switchMap(() =>
              from([adminCapabilitySuggestionCrudEvents.loadAllRequested({})])
            )
          ),
      };
    }
  )
);
